// src/pages/TelephonyOverview.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Server, RefreshCw, Hash, Activity, Gauge } from 'lucide-react';
import { Link } from 'react-router-dom';
import { providersService, ProviderOverview, UsageRollup } from 'api/services/providers.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { useAuth } from 'hooks/useAuth';
import { isPlatformTier, PLATFORM_CARRIERS_COPY } from './telephonyAccessRules';

const TelephonyOverview: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  // Probing every trunk is the platform admin's; anyone else just reloads their own trunks.
  const isMaster = isPlatformTier(currentOrganization?.role || user?.role);
  const [rows, setRows] = useState<ProviderOverview[]>([]);
  const [usage, setUsage] = useState<UsageRollup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    const [ov, us] = await Promise.all([providersService.overview(), providersService.usage()]);
    if (ov.success) setRows(ov.data || []); else setError(ov.error || 'Failed to load');
    if (us.success) setUsage(us.data || null);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setChecking(true);
    if (isMaster) await providersService.healthCheckAll();
    await load();
    setChecking(false);
  };

  if (loading) return <LoadingSpinner />;

  const totals = rows.reduce((a, r) => ({
    cps: a.cps + (r.cpsLimit || 0),
    channels: a.channels + (r.maxChannels || 0),
    live: a.live + (r.liveChannels || 0),
    numbers: a.numbers + r.numbersTotal,
    available: a.available + r.numbersAvailable,
    allocated: a.allocated + r.numbersAllocated,
    up: a.up + (r.healthy ? 1 : 0)
  }), { cps: 0, channels: 0, live: 0, numbers: 0, available: 0, allocated: 0, up: 0 });

  const bar = (used: number, total: number) => {
    const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
    return (
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
      </div>
    );
  };

  const utilBar = (used: number, total: number | null | undefined) => {
    const cap = total || 0;
    const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
    const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
    return (
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Telephony Overview</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Carrier trunks (tata1 / tata2 / …) — health, capacity and number inventory at a glance.</p>
        </div>
        <button onClick={refresh} disabled={checking} className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}

      {/* top-line totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Trunks', value: `${totals.up}/${rows.length} up`, icon: Server },
          { label: 'Live calls', value: `${totals.live}/${totals.channels}`, icon: Activity },
          { label: 'Total CPS', value: totals.cps, icon: Gauge },
          { label: 'Numbers', value: totals.numbers, icon: Hash },
          { label: 'Available', value: totals.available, icon: Hash },
        ].map(s => (
          <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider"><s.icon className="w-4 h-4" />{s.label}</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      {/* per-trunk cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map(r => (
          <div key={r.id} className={`bg-white dark:bg-gray-800 rounded-lg shadow p-5 border-l-4 ${r.healthy ? 'border-emerald-500' : 'border-red-500'}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{r.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{r.engineType} · {r.region || '—'} · {r.routingMode}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${r.lastCheckedAt == null ? 'bg-gray-100 text-gray-500' : r.healthy ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                {r.lastCheckedAt == null ? 'unchecked' : r.healthy ? `UP${r.latencyMs != null ? ` ${r.latencyMs}ms` : ''}` : 'DOWN'}
              </span>
            </div>

            <div className="space-y-2 text-sm">
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Live channels</span>
                  <span className="font-medium text-gray-900 dark:text-white">{r.liveChannels ?? 0} / {r.maxChannels ?? '∞'} in use</span>
                </div>
                {utilBar(r.liveChannels ?? 0, r.maxChannels)}
              </div>
              <div className="flex justify-between"><span className="text-gray-500">CPS</span><span className="font-medium text-gray-900 dark:text-white">{r.liveCps ?? 0} now · {r.cpsLimit ?? '∞'} max</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Channels</span><span className="font-medium text-gray-900 dark:text-white">{r.maxChannels ?? '∞'} <span className="text-xs text-gray-400">({r.capacityMode === 'PER_ORG' ? 'per-org' : 'pool'})</span></span></div>
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Numbers</span><span>{r.numbersAllocated}/{r.numbersTotal} allocated</span></div>
                {bar(r.numbersAllocated, r.numbersTotal)}
                <div className="flex justify-between text-[11px] text-gray-400 mt-0.5"><span className="text-green-600">{r.numbersAvailable} available</span><span>{r.numbersTotal} total</span></div>
              </div>
            </div>

            {!r.healthy && r.healthDetail && <p className="text-xs text-red-500 mt-3 truncate" title={r.healthDetail}>⚠ {r.healthDetail}</p>}
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex gap-3 text-xs">
              <Link to="/telephony/providers" className="text-blue-600 hover:underline">Manage</Link>
              <Link to="/phones" className="text-blue-600 hover:underline">Numbers</Link>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            {isMaster
              ? <>No providers yet. <Link to="/telephony/providers" className="text-blue-600 hover:underline">Add a trunk</Link> to get started.</>
              : PLATFORM_CARRIERS_COPY}
          </div>
        )}
      </div>

      {/* usage rollup (last 7 days) */}
      {usage && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 dark:text-white">Usage by trunk · last 7 days</h2>
            <span className="text-xs text-gray-500">{usage.totals.calls} calls · {usage.totals.minutes} min</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>{['Trunk', 'Calls', 'Inbound', 'Outbound', 'Minutes'].map(h => (
                  <th key={h} className={`px-5 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Trunk' ? 'text-left' : 'text-right'}`}>{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {usage.providers.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-center text-sm text-gray-500">No calls in this window.</td></tr>}
                {usage.providers.map((u, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-5 py-2 text-sm font-medium text-gray-900 dark:text-white">{u.provider}</td>
                    <td className="px-5 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{u.calls}</td>
                    <td className="px-5 py-2 text-sm text-right text-gray-600 dark:text-gray-400">{u.inbound}</td>
                    <td className="px-5 py-2 text-sm text-right text-gray-600 dark:text-gray-400">{u.outbound}</td>
                    <td className="px-5 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{u.minutes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelephonyOverview;
